/**
 * Data + lifecycle mutations for the Question Bank detail view.
 *
 * Owns: items + usage fetch, bank/item lifecycle transitions, item retire.
 * Dialog open-state stays in the BankDetail component (pure UI state).
 *
 * Extracted verbatim from QuestionBanksPage.jsx during the Phase 5 refactor
 * (no behaviour change).
 */
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { useApi } from './useApi';

export const isUnpublished = (it) => !['published', 'retired'].includes(it.status);

export function useBankDetail(bankId, onChanged) {
  const { get, patch, post, del } = useApi();
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(true);
  const [usage, setUsage] = useState(null); // { quizzes: [], live_sessions: [], total }

  const loadItems = useCallback(async () => {
    setLoadingItems(true);
    try {
      const res = await get(`/question-banks/${bankId}/items?limit=200`);
      setItems(res.items || []);
    } catch { toast.error('Failed to load items'); }
    finally { setLoadingItems(false); }
  }, [get, bankId]);

  const loadUsage = useCallback(async () => {
    try {
      const res = await get(`/question-banks/${bankId}/usage`);
      setUsage(res);
    } catch { /* non-fatal — block stays hidden */ }
  }, [get, bankId]);

  useEffect(() => { loadItems(); loadUsage(); }, [loadItems, loadUsage]);

  const handleTransitionBank = async (target, excludeItemIds = []) => {
    if (target === 'retired'
        && !window.confirm('Retire this entire bank? It becomes read-only for everyone.')) return;
    try {
      await patch(`/question-banks/${bankId}/lifecycle`, { target_status: target, exclude_item_ids: excludeItemIds });
      toast.success(`Bank → ${target}`);
      if (target === 'published') loadItems();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Transition failed'); }
  };

  const handlePublishAllItems = async (excludeItemIds = []) => {
    try {
      const before = items.filter(it => it.status === 'published').length;
      const res = await post(`/question-banks/${bankId}/publish-items`, { exclude_item_ids: excludeItemIds });
      const n = Math.max(0, res.published_item_count - before);
      toast.success(`${n} question${n === 1 ? '' : 's'} now published`);
      loadItems();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Publish failed'); }
  };

  const handleTransitionItem = async (itemId, target) => {
    if (target === 'retired'
        && !window.confirm('Retire this question? It becomes read-only and leaves the assessment pool. Historical attempts remain valid.')) return;
    try {
      await patch(`/question-banks/${bankId}/items/${itemId}/lifecycle`, { target_status: target });
      toast.success(`Item → ${target}`);
      loadItems();
    } catch (e) { toast.error(e.response?.data?.detail || 'Transition failed'); }
  };

  const handleRetireItem = async (itemId) => {
    if (!window.confirm('Retire this item? Historical attempts remain valid.')) return;
    try {
      await del(`/question-banks/${bankId}/items/${itemId}`);
      toast.success('Item retired'); loadItems();
    } catch (e) { toast.error(e.response?.data?.detail || 'Retire failed'); }
  };

  return {
    items,
    loadingItems,
    usage,
    loadItems,
    handleTransitionBank,
    handlePublishAllItems,
    handleTransitionItem,
    handleRetireItem,
  };
}
